import { useEffect, useMemo, useState } from 'react'
import { AlertTriangle, Star } from 'lucide-react'
import { fieldLabel, fieldWrapper, inputBase } from '@/lib/form-styles'
import { apiErrorText } from '@/lib/api-error'
import { bulkAssign, fetchCandidates } from '../../api/work-cockpit.api'
import type { AssignmentCandidates, AssignmentConflict } from '../../types/cockpit.types'
import { formatStunden, heuteIso, konfliktText, montagVon, plusTage, zeitraumKurz } from './cockpit-labels'

interface Auswahl {
  plannedHours: string
  isLead: boolean
}

/**
 * AP-35 Detailblatt 11 5.2 — **Einteilen.**
 *
 * Aus Karte, Liste, Gruppe, Mehrfachauswahl und Woche derselbe Dialog.
 * Zweistufig: „Prüfen" fragt den Server mit `dryRun`, was herauskäme, und
 * zeigt die Konflikte; „Einteilen" schreibt — auch mit Konflikten, denn die
 * sind Hinweise, keine Sperren (5.4, 12.2-5).
 */
export function AssignDialog({
  stepIds,
  vorbelegt,
  onClose,
  onDone,
}: {
  stepIds: string[]
  vorbelegt?: { userId?: string; startsOn?: string; endsOn?: string }
  onClose: () => void
  onDone: () => void
}) {
  const [daten, setDaten] = useState<AssignmentCandidates | null>(null)
  const [von, setVon] = useState(vorbelegt?.startsOn ?? '')
  const [bis, setBis] = useState(vorbelegt?.endsOn ?? '')
  const [gewaehlt, setGewaehlt] = useState<Record<string, Auswahl>>({})
  const [note, setNote] = useState('')
  const [suche, setSuche] = useState('')
  const [konflikte, setKonflikte] = useState<AssignmentConflict[] | null>(null)
  const [laeuft, setLaeuft] = useState(false)
  const [fehler, setFehler] = useState<string | null>(null)

  useEffect(() => {
    let aktiv = true
    fetchCandidates(stepIds, vorbelegt?.startsOn, vorbelegt?.endsOn)
      .then((d) => {
        if (!aktiv) return
        setDaten(d)
        if (!vorbelegt?.startsOn) setVon(d.defaults.startsOn)
        if (!vorbelegt?.endsOn) setBis(d.defaults.endsOn)
        if (vorbelegt?.userId) setGewaehlt({ [vorbelegt.userId]: { plannedHours: '', isLead: false } })
      })
      .catch((e) => setFehler(apiErrorText(e, 'Die Mitarbeiter konnten nicht geladen werden.')))
    return () => {
      aktiv = false
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stepIds.join(',')])

  // Jede Änderung macht eine frühere Prüfung wertlos.
  useEffect(() => setKonflikte(null), [von, bis, gewaehlt, note])

  const anzahl = Object.keys(gewaehlt).length
  const soll = daten?.steps.reduce((s, x) => s + (x.plannedHoursTotal ?? 0), 0) ?? 0
  const namen = useMemo(() => new Map((daten?.users ?? []).map((u) => [u.id, u.name])), [daten])
  const schritte = useMemo(() => new Map((daten?.steps ?? []).map((s) => [s.id, `${s.number} ${s.activityName}`])), [daten])

  const gefiltert = (daten?.users ?? []).filter((u) => !suche.trim() || u.name.toLowerCase().includes(suche.trim().toLowerCase()))

  const heute = heuteIso()
  const schnell = [
    { label: 'diese Woche', von: heute, bis: plusTage(montagVon(heute), 4) },
    { label: 'nächste Woche', von: plusTage(montagVon(heute), 7), bis: plusTage(montagVon(heute), 11) },
    { label: `nur ${zeitraumKurz(von || heute, von || heute)}`, von: von || heute, bis: von || heute },
  ]

  function payload() {
    return {
      stepIds,
      users: Object.entries(gewaehlt).map(([userId, a]) => ({
        userId,
        isLead: a.isLead,
        ...(a.plannedHours.trim() !== '' ? { plannedHours: Number(a.plannedHours.replace(',', '.')) } : {}),
      })),
      startsOn: von,
      endsOn: bis,
      note: note.trim() || undefined,
    }
  }

  async function pruefen() {
    setLaeuft(true)
    setFehler(null)
    try {
      const r = await bulkAssign(payload(), true)
      setKonflikte(r.conflicts)
      if (r.conflicts.length === 0) await schreiben()
    } catch (e) {
      setFehler(apiErrorText(e, 'Die Prüfung hat nicht geklappt.'))
    } finally {
      setLaeuft(false)
    }
  }

  async function schreiben() {
    setLaeuft(true)
    setFehler(null)
    try {
      await bulkAssign(payload(), false)
      onDone()
    } catch (e) {
      setFehler(apiErrorText(e, 'Die Einteilung hat nicht geklappt.'))
    } finally {
      setLaeuft(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div
        className="flex max-h-[90vh] w-full max-w-2xl flex-col gap-4 overflow-hidden rounded-lg border border-border bg-background p-6 shadow-xl"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-label="Einteilen"
      >
        <h2 className="text-lg font-semibold text-primary">Einteilen</h2>

        {daten && (
          <div className="max-h-24 overflow-y-auto rounded-md border border-border bg-muted/30 px-3 py-2 text-xs">
            {daten.steps.map((s) => (
              <div key={s.id} className="flex gap-2">
                <span className="text-muted-foreground">{s.projectNumber}</span>
                <span className="truncate">
                  {s.positionLabel} · <span className="font-mono">{s.number}</span> {s.activityName}
                </span>
                <span className="ml-auto whitespace-nowrap tabular-nums text-muted-foreground">
                  {formatStunden(s.plannedHoursTotal)} h{s.dueAt ? ` · fällig ${zeitraumKurz(s.dueAt, s.dueAt)}` : ''}
                </span>
              </div>
            ))}
          </div>
        )}

        <div className="grid gap-3 sm:grid-cols-2">
          <div className={fieldWrapper}>
            <label className={fieldLabel}>von</label>
            <input type="date" className={inputBase} value={von} onChange={(e) => setVon(e.target.value)} />
          </div>
          <div className={fieldWrapper}>
            <label className={fieldLabel}>bis</label>
            <input type="date" className={inputBase} value={bis} min={von} onChange={(e) => setBis(e.target.value)} />
          </div>
        </div>
        <div className="-mt-2 flex flex-wrap gap-2 text-xs">
          {schnell.map((s) => (
            <button
              key={s.label}
              type="button"
              className="rounded-full border border-border px-2 py-0.5 hover:bg-muted"
              onClick={() => {
                setVon(s.von)
                setBis(s.bis)
              }}
            >
              {s.label}
            </button>
          ))}
        </div>

        <div className="flex min-h-0 flex-1 flex-col gap-2">
          <div className="flex items-center gap-2">
            <span className={fieldLabel}>Mitarbeiter</span>
            <input className={`${inputBase} ml-auto max-w-[12rem] py-1 text-xs`} placeholder="Suche" value={suche} onChange={(e) => setSuche(e.target.value)} />
          </div>
          <div className="min-h-[8rem] flex-1 overflow-y-auto rounded-md border border-border">
            {!daten && <p className="p-3 text-sm text-muted-foreground">Laden …</p>}
            {gefiltert.map((u) => {
              const a = gewaehlt[u.id]
              // Die Vorgabe gilt je Schritt (Soll ÷ Anzahl); bei mehreren Schritten gibt es
              // keine eine Zahl, die man hier zeigen könnte.
              const vorgabe = stepIds.length === 1 && anzahl > 0 && soll > 0 ? formatStunden(soll / anzahl) : 'je Soll'
              return (
                <div key={u.id} className={`flex items-center gap-2 border-b border-border px-3 py-1.5 text-sm last:border-0 ${a ? 'bg-primary/5' : ''}`}>
                  <input
                    type="checkbox"
                    checked={!!a}
                    aria-label={u.name}
                    onChange={(e) =>
                      setGewaehlt((alt) => {
                        const neu = { ...alt }
                        if (e.target.checked) neu[u.id] = { plannedHours: '', isLead: false }
                        else delete neu[u.id]
                        return neu
                      })
                    }
                  />
                  <span className="min-w-0 flex-1 truncate" title={u.name}>
                    {u.name}
                    {u.qualification && <span className="ml-1 text-xs text-muted-foreground">· {u.qualification}</span>}
                    {u.matchesQualification && <span className="ml-1 rounded bg-green-100 px-1 text-xs text-green-800 dark:bg-green-900 dark:text-green-200">passt</span>}
                    {u.inPhaseTeam && <span className="ml-1 rounded bg-muted px-1 text-xs text-muted-foreground">Team</span>}
                    {!u.canBook && <span className="ml-1 text-xs text-muted-foreground">(ohne App)</span>}
                  </span>
                  <span className="whitespace-nowrap text-xs tabular-nums text-muted-foreground" title="Stunden diese Woche">
                    {formatStunden(u.weekHours)} h
                  </span>
                  {u.absences.length > 0 && (
                    <span className="flex items-center gap-0.5 whitespace-nowrap text-xs text-yellow-700 dark:text-yellow-300" title={u.absences.map((x) => `${x.date} ${x.type}`).join(', ')}>
                      <AlertTriangle className="h-3 w-3" /> {u.absences[0].type}
                    </span>
                  )}
                  {a && (
                    <>
                      <input
                        className="w-16 shrink-0 rounded-md border border-input bg-background px-2 py-0.5 text-right text-xs"
                        placeholder={vorgabe}
                        title={stepIds.length > 1 ? 'leer = Soll je Schritt ÷ Anzahl' : undefined}
                        value={a.plannedHours}
                        aria-label={`Stunden ${u.name}`}
                        onChange={(e) => setGewaehlt((alt) => ({ ...alt, [u.id]: { ...a, plannedHours: e.target.value } }))}
                      />
                      <button
                        type="button"
                        title="führt"
                        className={a.isLead ? 'text-primary' : 'text-muted-foreground'}
                        onClick={() =>
                          setGewaehlt((alt) =>
                            Object.fromEntries(Object.entries(alt).map(([id, x]) => [id, { ...x, isLead: id === u.id ? !x.isLead : false }])),
                          )
                        }
                      >
                        <Star className="h-4 w-4" fill={a.isLead ? 'currentColor' : 'none'} />
                      </button>
                    </>
                  )}
                </div>
              )
            })}
          </div>
        </div>

        <div className={fieldWrapper}>
          <label className={fieldLabel}>Anmerkung</label>
          <input className={inputBase} maxLength={200} value={note} placeholder="nur vormittags, mit Kran …" onChange={(e) => setNote(e.target.value)} />
        </div>

        {konflikte && konflikte.length > 0 && (
          <div className="max-h-32 overflow-y-auto rounded-md border border-yellow-500/60 bg-muted/60 px-3 py-2 text-xs text-foreground">
            <p className="mb-1 font-medium">Hinweise — die Einteilung ist trotzdem möglich:</p>
            <ul className="list-disc space-y-0.5 pl-4">
              {konflikte.map((k, i) => (
                <li key={i}>{konfliktText(k, (id) => (id ? namen.get(id) ?? '' : ''), (id) => (id ? schritte.get(id) ?? '' : ''))}</li>
              ))}
            </ul>
          </div>
        )}

        {fehler && <p className="text-sm text-destructive">{fehler}</p>}

        <div className="flex justify-end gap-2">
          <button type="button" className="rounded-md border border-border px-4 py-2 text-sm" onClick={onClose}>
            Abbrechen
          </button>
          {konflikte && konflikte.length > 0 ? (
            <button
              type="button"
              className="rounded-md bg-primary px-4 py-2 text-sm text-primary-foreground disabled:opacity-50"
              disabled={laeuft}
              onClick={() => void schreiben()}
            >
              Trotzdem einteilen
            </button>
          ) : (
            <button
              type="button"
              className="rounded-md bg-primary px-4 py-2 text-sm text-primary-foreground disabled:opacity-50"
              disabled={laeuft || anzahl === 0 || !von || !bis || bis < von}
              onClick={() => void pruefen()}
            >
              {laeuft ? 'Prüft …' : `Einteilen (${stepIds.length} × ${anzahl})`}
            </button>
          )}
        </div>
      </div>
    </div>
  )
}
